import React from "react";
import { Mail, Phone, MapPin, Github, Linkedin, MessageCircle, Send } from "lucide-react";

/**
 * Footer Component
 * Displays contact information, links, and company details at the bottom of the page
 * Features: Contact info, social links, quick links, and newsletter signup
 */
const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-900 text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <div>
              <h3 className="text-2xl font-bold text-white mb-2">ReXume</h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                HR과 인재를 직접 연결하는 혁신적인 채용 플랫폼입니다. 
                더 나은 채용 경험을 제공합니다.
              </p>
            </div>
            <div className="flex space-x-4">
              <a
                href="https://github.com/ReXume"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="GitHub"
              >
                <Github className="w-5 h-5" />
              </a>
              <a
                href="https://linkedin.com/company/rexume"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-white">빠른 링크</h4>
            <ul className="space-y-2">
              <li>
                <a
                  href="/"
                  className="text-gray-300 hover:text-white transition-colors text-sm"
                >
                  홈
                </a>
              </li>
              <li>
                <a
                  href="/search"
                  className="text-gray-300 hover:text-white transition-colors text-sm"
                >
                  인재 검색
                </a>
              </li>
              <li>
                <a
                  href="/jobs"
                  className="text-gray-300 hover:text-white transition-colors text-sm"
                >
                  채용 공고
                </a>
              </li>
              <li>
                <a
                  href="/profile/kimdev"
                  className="text-gray-300 hover:text-white transition-colors text-sm"
                >
                  프로필 예시
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-white">문의 및 제안</h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Mail className="w-4 h-4 text-blue-400 flex-shrink-0" />
                <div>
                  <p className="text-gray-300 text-sm">이메일</p>
                  <a
                    href="mailto:contact@rexume.com"
                    className="text-white hover:text-blue-400 transition-colors text-sm"
                  >
                    contact@rexume.com
                  </a>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-4 h-4 text-blue-400 flex-shrink-0" />
                <div>
                  <p className="text-gray-300 text-sm">전화</p>
                  <a
                    href="tel:+82-2-1234-5678"
                    className="text-white hover:text-blue-400 transition-colors text-sm"
                  >
                    +82-2-1234-5678
                  </a>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <MapPin className="w-4 h-4 text-blue-400 flex-shrink-0" />
                <div>
                  <p className="text-gray-300 text-sm">주소</p>
                  <p className="text-white text-sm">
                    서울특별시 강남구 테헤란로 123
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Newsletter & Feedback */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-white">소식 받기</h4>
            <p className="text-gray-300 text-sm">
              새로운 기능과 업데이트 소식을 받아보세요.
            </p>
            <div className="space-y-3">
              <div className="flex">
                <input
                  type="email"
                  placeholder="이메일 주소"
                  className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-l-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-r-lg transition-colors">
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <div className="flex items-center space-x-2">
                <MessageCircle className="w-4 h-4 text-green-400" />
                <span className="text-gray-300 text-sm">
                  피드백은 언제나 환영합니다!
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-gray-400 text-sm">
              © {currentYear} ReXume. All rights reserved.
            </div>
            <div className="flex space-x-6 text-sm">
              <a
                href="/privacy"
                className="text-gray-400 hover:text-white transition-colors"
              >
                개인정보처리방침
              </a>
              <a
                href="/terms"
                className="text-gray-400 hover:text-white transition-colors"
              >
                이용약관
              </a>
              <a
                href="/support"
                className="text-gray-400 hover:text-white transition-colors"
              >
                고객지원
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
